import { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Permission, Role } from '@/types';
import { hasUserPermission, canAccessProjects, canActAsProvider } from '@/lib/permissions';
import { COLORS } from '@/styles/designTokens';

interface TabItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  id: string;
}

interface MobileBottomBarProps {
  role: Role;
  permissions?: Permission[];
}

/**
 * Barre de navigation mobile iOS-style avec bulle flottante
 * Utilise les permissions RBAC pour déterminer les onglets visibles
 */
export function MobileBottomBar({ role, permissions }: MobileBottomBarProps) {
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Générer les onglets selon les permissions (React Compiler mémoïse automatiquement)
  const allTabs: TabItem[] = [];

  // Dashboard - toujours visible
  allTabs.push({ to: '/', label: 'Accueil', icon: <span className="text-2xl">🏠︎</span>, id: 'home' });

  // Projets - visible pour tous ceux qui ont accès à des projets
  if (canAccessProjects(role, permissions)) {
    allTabs.push({ to: '/projects', label: 'Projets', icon: <span className="text-2xl">📁︎</span>, id: 'projects' });
  }

  // Tâches - admin uniquement
  if (hasUserPermission(role, permissions, Permission.MANAGE_SETTINGS)) {
    allTabs.push({ to: '/tache', label: 'Tache', icon: <span className="text-2xl">✅</span>, id: 'tache' });
  }

  // Calendrier éditorial - visible si accès aux projets
  if (canAccessProjects(role, permissions)) {
    allTabs.push({ to: '/editorial-calendar', label: 'Calendrier', icon: <span className="text-2xl">🚀</span>, id: 'editorial-calendar' });
  }

  // Entreprises - admin uniquement
  if (hasUserPermission(role, permissions, Permission.MANAGE_CLIENTS)) {
    allTabs.push({ to: '/clients', label: 'Entreprises', icon: <span className="text-2xl">🏢</span>, id: 'clients' });
  }

  // Utilisateurs - admin uniquement
  if (hasUserPermission(role, permissions, Permission.MANAGE_USERS)) {
    allTabs.push({ to: '/users', label: 'Users', icon: <span className="text-2xl">👤︎</span>, id: 'users' });
  }

  // Factures admin - validation des factures
  if (hasUserPermission(role, permissions, Permission.VALIDATE_INVOICES)) {
    allTabs.push({ to: '/invoices', label: 'Factures', icon: <span className="text-2xl">📄︎</span>, id: 'invoices' });
  }

  // Mes factures - prestataires ou chefs avec ACT_AS_PROVIDER
  if (canActAsProvider(role, permissions)) {
    // Ne pas afficher si on a déjà VALIDATE_INVOICES
    if (!hasUserPermission(role, permissions, Permission.VALIDATE_INVOICES)) {
      allTabs.push({ to: '/my-invoices', label: 'Factures', icon: <span className="text-2xl">💰︎</span>, id: 'my-invoices' });
    }
  }

  // Mes infos - prestataires ou chefs avec ACT_AS_PROVIDER
  if (canActAsProvider(role, permissions)) {
    allTabs.push({ to: '/provider-info', label: 'Infos', icon: <span className="text-2xl">😊</span>, id: 'infos' });
  }

  // Trouver l'onglet actif
  const activeTabIndex = (() => {
    const currentPath = location.pathname;

    // Vérifier d'abord les correspondances exactes
    const exactMatch = allTabs.findIndex(tab => tab.to === currentPath);
    if (exactMatch >= 0) return exactMatch;

    // Ensuite vérifier les correspondances par préfixe pour les sous-routes
    for (let i = 0; i < allTabs.length; i++) {
      const tab = allTabs[i];
      if (currentPath.startsWith(tab.to) && tab.to !== '/') {
        return i;
      }
    }

    return -1;
  })();

  const activeColor = COLORS.gray[900];

  // Scroll vers l'onglet actif au chargement/changement de route
  useEffect(() => {
    if (scrollContainerRef.current && activeTabIndex >= 0) {
      const container = scrollContainerRef.current;
      const activeTab = container.children[activeTabIndex] as HTMLElement;
      if (activeTab) {
        const containerWidth = container.offsetWidth;
        const tabLeft = activeTab.offsetLeft;
        const tabWidth = activeTab.offsetWidth;
        const scrollPosition = tabLeft - (containerWidth / 2) + (tabWidth / 2);

        container.scrollTo({
          left: scrollPosition,
          behavior: 'smooth'
        });
      }
    }
  }, [activeTabIndex, location.pathname]);

  return (
    <>
      {/* Barre de navigation iOS style - Bulle flottante */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-header flex justify-center px-4 pb-3"
        style={{ paddingBottom: `calc(env(safe-area-inset-bottom) + 0.75rem)` }}
      >
        {/* Bulle/pill iOS style avec effet glassmorphism */}
        <motion.nav
          className="relative"
          initial={false}
          style={{
            maxWidth: 'calc(100vw - 2rem)',
            width: 'fit-content',
            minWidth: '200px'
          }}
        >
          {/* Fond avec effet de verre dépoli et ombre (style iOS) */}
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.25)',
              backdropFilter: 'blur(40px) saturate(180%)',
              WebkitBackdropFilter: 'blur(40px) saturate(180%)',
              boxShadow: `
                0 8px 32px rgba(0, 0, 0, 0.12),
                0 2px 8px rgba(0, 0, 0, 0.08),
                inset 0 1px 0 rgba(255, 255, 255, 0.6),
                inset 0 -1px 0 rgba(0, 0, 0, 0.05)
              `,
              border: '0.5px solid rgba(255, 255, 255, 0.5)'
            }}
          />

          {/* Reflet sur le bord supérieur (effet iOS) */}
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 0) 30%, transparent 100%)',
              borderTop: '0.5px solid rgba(255, 255, 255, 0.9)'
            }}
          />

          {/* Reflet sur le bord inférieur (effet iOS) */}
          <div
            className="absolute inset-0 rounded-full pointer-events-none"
            style={{
              background: 'linear-gradient(0deg, rgba(0, 0, 0, 0.03) 0%, transparent 30%)',
              borderBottom: '0.5px solid rgba(0, 0, 0, 0.1)'
            }}
          />

          {/* Contenu scrollable horizontal */}
          <div
            ref={scrollContainerRef}
            className="relative flex items-center gap-0.5 px-2 py-2 overflow-x-auto overflow-y-hidden scrollbar-hide"
            style={{
              WebkitOverflowScrolling: 'touch',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              minHeight: '60px',
              touchAction: 'pan-x'
            }}
            onScroll={(e) => {
              // Empêcher le scroll vertical accidentel
              const target = e.currentTarget;
              if (target.scrollTop !== 0) {
                target.scrollTop = 0;
              }
            }}
          >
            {/* Tous les onglets avec scroll horizontal - SEULEMENT LES ICÔNES */}
            {allTabs.map((tab, index) => {
              const isActive = activeTabIndex === index;
              return (
                <Link
                  key={tab.id}
                  to={tab.to}
                  className="relative flex items-center justify-center min-w-[56px] w-14 h-14 rounded-full transition-all duration-200 active:scale-95 flex-shrink-0"
                  style={{
                    backgroundColor: isActive ? `${activeColor}15` : 'transparent',
                    touchAction: 'manipulation',
                    WebkitTapHighlightColor: 'transparent'
                  }}
                  aria-label={tab.label}
                >
                  {/* Indicateur actif animé (point en haut) */}
                  {isActive && (
                    <motion.div
                      layoutId="activeIndicator"
                      className="absolute -top-0.5 left-1/2 w-1.5 h-1.5 rounded-full pointer-events-none"
                      style={{
                        backgroundColor: activeColor,
                        x: '-50%'
                      }}
                      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                    />
                  )}

                  {/* Icône seulement */}
                  <motion.div
                    className="pointer-events-none"
                    animate={{
                      scale: isActive ? 1.15 : 1,
                    }}
                    transition={{ duration: 0.2 }}
                    style={{
                      color: isActive ? activeColor : COLORS.gray[500],
                      filter: isActive ? 'grayscale(0)' : 'grayscale(0.3)'
                    }}
                  >
                    {tab.icon}
                  </motion.div>
                </Link>
              );
            })}
          </div>
        </motion.nav>
      </div>
    </>
  );
}
